// src/components/Overview/Overview.js
import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  Tooltip,
  ResponsiveContainer,
  LineChart,
  Line,
} from "recharts";
import { useTranslation } from "react-i18next";
import "./Overview.css";

import Filter_Store from "../common/Filter_store";
import MobileFiltersDrawer from "../common/MobileFiltersDrawer";
import TimeRangePicker from "../common/TimeRangePicker";
import LangSwitcher from "../common/LangSwitcher";
import { useAuth } from "../../auth/AuthContext";
import { useDateRange } from "../../context/DateRangeContext";
import { useFilters } from "../../context/FilterContext";

const API_BASE = "https://boss.kfdtech.cn/api";

/* ---------- 工具函数 ---------- */
const fmtShort = (n, digits = 2) => {
  if (n == null || isNaN(n)) return "";
  const abs = Math.abs(n);
  const sign = n < 0 ? "-" : "";
  const trim = (x) =>
    x.toFixed(digits).replace(/\.0+$|(\.\d*[1-9])0+$/, "$1");
  if (abs >= 1e9) return sign + trim(abs / 1e9) + "B";
  if (abs >= 1e6) return sign + trim(abs / 1e6) + "M";
  if (abs >= 1e3) return sign + trim(abs / 1e3) + "K";
  return String(n);
};

// K/M/B 缩写（小于 1000 取整数，大于等于 1000 缩写）
const formatAbbr = (n, digits = 2) => {
  if (n == null || isNaN(n)) return "";
  const v = Number(n);
  const abs = Math.abs(v);
  const sign = v < 0 ? "-" : "";

  const trim = (x) => x.toFixed(digits).replace(/\.0+$|(\.\d*[1-9])0+$/, "$1");

  if (abs < 1000) return String(Math.round(v));

  if (abs >= 1e9) return `${sign}${trim(abs / 1e9)} B`;
  if (abs >= 1e6) return `${sign}${trim(abs / 1e6)} M`;
  if (abs >= 1e3) return `${sign}${trim(abs / 1e3)} K`;

  return String(v);
};

const toYm = (d) => {
  if (!d) return "";
  const date = typeof d === "string" ? new Date(d) : d;
  if (isNaN(date)) return "";
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, "0");
  return `${y}-${m}`;
};

function Empty() {
  return <div className="empty">No data</div>;
}

const mergeLineSeries = (lines) => {
  const map = new Map();

  (lines || []).forEach((s) => {
    (s?.series || []).forEach((p) => {
      const x = p?.x ?? "";
      const value = p?.value;

      if (!map.has(x)) map.set(x, { x, "All Brand": null });

      // 如果 value 是 null，就保持 null
      if (value == null) {
        map.get(x)["All Brand"] = null;
      } else {
        map.get(x)["All Brand"] = Number(value);
      }
    });
  });

  const arr = [...map.values()].sort((a, b) =>
    String(a.x).localeCompare(String(b.x))
  );

  return { data: arr, keys: ["All Brand"] };
};

/* ---------- GrowthCard ---------- */
function GrowthCard({ title, mode, onToggle, line, table, t }) {
  const merged = useMemo(() => mergeLineSeries(line), [line]);
  const noLine = !merged.data.length;
  const noTable = !table || !table.length;

  const [page, setPage] = useState(1);
  const PAGE_SIZE = 5;

  const total = table?.length || 0;
  const pages = Math.max(1, Math.ceil(total / PAGE_SIZE));
  const start = (page - 1) * PAGE_SIZE;
  const pageRows = table?.slice(start, start + PAGE_SIZE) || [];
  const blanks = Array.from({
    length: Math.max(0, PAGE_SIZE - pageRows.length),
  });

  const goPrev = () => setPage((p) => Math.max(1, p - 1));
  const goNext = () => setPage((p) => Math.min(pages, p + 1));

  useEffect(() => {
    setPage(1);
  }, [table]);

  return (
    <div className="growth-card">
      <div className="card-tools">
        <h4>{t(title)}</h4>
        <button className="toggle btn-toggle top-right" onClick={onToggle}>
          {mode === "chart" ? t("table") : t("chart")}
        </button>
      </div>

      <div className="growth-body">
        {mode === "chart" ? (
          noLine ? (
            <Empty />
          ) : (
            <div className="line-wrap">
              {/* ✅ 用 HTML 做 label，避免 Recharts SVG label 被截断 */}
              <div className="y-label">
                {title === "yoyGrowthCard"
                  ? t("YoY Growth %")
                  : t("MtoM Growth %")}
              </div>

              <ResponsiveContainer width="100%" height={220}>
                <LineChart
                  data={merged.data}
                  margin={{ top: 10, right: 20, left: 20, bottom: 10 }}
                >
                  <XAxis
                    dataKey="x"
                    tick={{ fontSize: 11, fill: "#555" }}
                    angle={-30}
                    textAnchor="end"
                    height={30}
                  />

                  <YAxis
                    tickFormatter={(v) => (v == null ? "" : `${v}%`)}
                    tick={{ fontSize: 11, fill: "#555" }}
                    width={60}
                  />

                  {merged.keys.map((k) => (
                    <Line
                      key={k}
                      type="monotone"
                      dataKey={k}
                      stroke="#3B82F6"
                      strokeWidth={2}
                      dot={{ r: 2 }}
                    />
                  ))}

                  <Tooltip
                    formatter={(v) => (v == null ? "N/A" : `${v}%`)}
                  />
                </LineChart>
              </ResponsiveContainer>
            </div>
          )
        ) : noTable ? (
          <Empty />
        ) : (
          <div className="table-wrap">
            <table className="mini-table">
              <thead>
                <tr>
                  <th>
                    {title === "yoyGrowthCard" ? t("year") : t("yearMonth")}
                  </th>

                  <th className="ta-right">{t("netSales")}</th>

                  <th className="ta-right">
                    {title === "yoyGrowthCard"
                      ? t("NetSales_lastyear")
                      : t("NetSales_lastmonth")}
                  </th>

                  <th className="ta-right">
                    {title === "yoyGrowthCard"
                      ? t("YoY Growth %")
                      : t("MtoM Growth %")}
                  </th>
                </tr>
              </thead>

              <tbody>
                {pageRows.map((r, i) => (
                  <tr key={i}>
                    <td>
                      {title === "yoyGrowthCard"
                        ? r.year ?? r.Year ?? ""
                        : r.YearMonth}
                    </td>

                    <td className="ta-right">{fmtShort(r.net_sales)}</td>

                    <td className="ta-right">
                      {fmtShort(
                        r.net_sales_last_year ??
                          r.net_sales_last_month ??
                          0
                      )}
                    </td>

                    <td className="ta-right">
                      {(() => {
                        const g = r.yoy_growth_pct ?? r.mom_growth_pct;
                        return g == null ? "N/A" : `${Number(g).toFixed(2)}%`;
                      })()}
                    </td>
                  </tr>
                ))}

                {blanks.map((_, i) => (
                  <tr key={`blank-${i}`}>
                    <td colSpan={4} style={{ height: "32px" }}></td>
                  </tr>
                ))}
              </tbody>
            </table>

            <div className="mini-pager right">
              <button
                className="pager-btn"
                disabled={page === 1}
                onClick={goPrev}
              >
                Prev
              </button>

              <span style={{ margin: "0 8px" }}>
                {page}/{pages}
              </span>

              <button
                className="pager-btn"
                disabled={page === pages}
                onClick={goNext}
              >
                Next
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

/* ---------- 主组件 ---------- */
export default function Overview() {
  const { authFetch } = useAuth();
  const { t, i18n } = useTranslation();
  const lang = i18n.language;
  const { dateRange } = useDateRange();
  const { filters, setFilters } = useFilters();
  const [rows, setRows] = useState([]);

  const [filterOpts, setFilterOpts] = useState({
    stores: [],
    platforms: [],
    brand2plats: {},
  });

  const [drawerOpen, setDrawerOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [data, setData] = useState(null);

  const [yoyMode, setYoyMode] = useState("chart");
  const [momMode, setMomMode] = useState("chart");

  const initializedRef = useRef(false);

  const filterLoading =
    filterOpts.stores.length === 0 && filterOpts.platforms.length === 0;

  /* ---------- 初始化 filters ---------- */
  useEffect(() => {
    let abort = false;

    (async () => {
      try {
        const resp = await authFetch(`${API_BASE}/filters`);
        const json = await resp.json();

        if (!resp.ok)
          throw new Error(json?.detail || json?.error || `HTTP ${resp.status}`);
        if (abort) return;

        const stores = json.stores || [];
        const platforms = json.platforms || [];
        const brand2plats = json.brand2plats || {};

        setFilterOpts({ stores, platforms, brand2plats });

        // 只在 Overview 第一次挂载时处理默认逻辑
        if (!initializedRef.current) {
          initializedRef.current = true;

          const hasContext =
            (filters?.stores?.length ?? 0) > 0 ||
            (filters?.platforms?.length ?? 0) > 0;

          // 如果 Context 已有 filters，就不覆盖
          if (!hasContext) {
            setFilters({
              stores: stores.slice(),
              platforms: platforms.slice(),
            });
          }
        }
      } catch (e) {
        if (!abort) setError(String(e?.message || e));
      }
    })();

    return () => {
      abort = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  /* ---------- 平台限制逻辑 ---------- */
  const allowedPlatforms = useMemo(() => {
    const sel = filters.stores;

    if (!sel?.length) {
      return (filterOpts.platforms || [])
        .filter((p) => typeof p === "string" && p.trim() !== "")
        .sort((a, b) => a.localeCompare(b));
    }

    const acc = new Set();
    sel.forEach((b) => {
      const plats = filterOpts.brand2plats?.[b];
      if (Array.isArray(plats)) {
        plats.forEach((p) => acc.add(p));
      }
    });

    return Array.from(acc)
      .filter((p) => typeof p === "string" && p.trim() !== "")
      .sort((a, b) => a.localeCompare(b));
  }, [filters.stores, filterOpts]);

  /* ---------- 请求参数 ---------- */
  const payload = useMemo(() => {
    const start = toYm(dateRange?.startDate);
    const end = toYm(dateRange?.endDate);

    const brands = filters?.stores || [];
    const plats = filters?.platforms || [];

    if (!start || !end || !brands.length || !plats.length) return null;

    return {
      YearMonth_List: [start, end],
      Brand_List: brands,
      Platform_List: plats,
      allow_current_month: dateRange?.mode === "custom",
    };
  }, [dateRange, filters]);

  /* ---------- 拉取 overview 数据 ---------- */
  useEffect(() => {
    if (filterLoading || !payload) {
      setData(null);
      return;
    }

    let abort = false;

    (async () => {
      try {
        setLoading(true);

        const resp = await authFetch(`${API_BASE}/overview`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(payload),
        });

        const json = await resp.json();

        if (!resp.ok)
          throw new Error(json?.detail || json?.error || `HTTP ${resp.status}`);

        if (!abort) setData(json);
      } catch (e) {
        if (!abort) setError(String(e?.message || e));
      } finally {
        if (!abort) setLoading(false);
      }
    })();

    return () => {
      abort = true;
    };
  }, [payload, filterLoading, authFetch]);

  const cards = data?.cards || {};
  const gmvByMonth = data?.charts?.gmvByMonth || [];
  const yoy = data?.growth?.yoy || { table: [], line: [] };
  const mom = data?.growth?.mom || { table: [], line: [] };

  const gmvData = gmvByMonth.map((r) => ({
    YearMonth: r.YearMonth,
    gmv: Number(r.gmv || 0),
  }));

  return (
    <div className="overview-layout">
      <div className="main-panel overview-scroll">
  <div className="overview-inner">
        <div className="page-topbar">
          <h1 className="site-title">{t("title")}</h1>
          <div className="only-mobile">
            <LangSwitcher />
          </div>
        </div>

        <div className="overview-header">
          <div className="page-subtitle">{t("Overview")}</div>

          <div className="header-right">
            <button
              className="filter-button-mobile"
              onClick={() => setDrawerOpen(true)}
            >
              筛选
            </button>

            <TimeRangePicker />
          </div>
        </div>

        {/* 统计卡片 */}
        <div className="stat-cards">
          <div className="card bg-blue">
            <div className="card-title">{t("gmv")}</div>
            <div className="card-value">{fmtShort(cards.gmv)}</div>
          </div>

          <div className="card bg-purple">
            <div className="card-title">{t("netSales")}</div>
            <div className="card-value">{fmtShort(cards.netSales)}</div>
          </div>

          <div className="card bg-blue">
            <div className="card-title">{t("refund")}</div>
            <div className="card-value">{fmtShort(cards.refund)}</div>
          </div>

          <div className="card bg-purple">
            <div className="card-title">{t("mediaCost")}</div>
            <div className="card-value">{fmtShort(cards.mediaCost)}</div>
          </div>
        </div>

        {/* GMV 图表 */}
        <div className="chart-section">
          <h3 className="section-title">{t("Overview_GMV_chat")}</h3>

          {!gmvData.length ? (
            <Empty />
          ) : (
            <ResponsiveContainer width="100%" height={300}>
              <BarChart data={gmvData}>
                <XAxis
                  dataKey="YearMonth"
                  tick={{ fontSize: 11, fill: "#555" }}
                  angle={-40}
                  textAnchor="end"
                  height={50}
                />

                <YAxis
                  tickFormatter={(v) => fmtShort(v)}
                  tick={{ fontSize: 12, fill: "#555" }}
                />

                <Tooltip formatter={(v) => fmtShort(v)} />

                <Bar
                  dataKey="gmv"
                  fill="#8cb4f0"
                  barSize={20}
                  radius={[10, 10, 10, 10]}
                />
              </BarChart>
            </ResponsiveContainer>
          )}
        </div>

        {/* 增长卡片 */}
        <div className="growth-section">
          <GrowthCard
            title="yoyGrowthCard"
            mode={yoyMode}
            onToggle={() =>
              setYoyMode((m) => (m === "chart" ? "table" : "chart"))
            }
            line={yoy.line}
            table={yoy.table}
            t={t}
          />

          <GrowthCard
            title="momGrowthCard"
            mode={momMode}
            onToggle={() =>
              setMomMode((m) => (m === "chart" ? "table" : "chart"))
            }
            line={mom.line}
            table={mom.table}
            t={t}
          />
        </div>

        {loading && (
          <div className="loading-overlay">
            <div className="spinner" />
            Loading…
          </div>
        )}

        {error && !loading && (
          <div className="loading-overlay">Error: {error}</div>
        )}
        </div>
</div>

      {/* 右侧筛选栏 */}
      <div className="filters-sidebar">
        <div className="filters-head">
          <LangSwitcher />
        </div>

        <Filter_Store
          loading={filterLoading}
          stores={filterOpts.stores}
          platforms={allowedPlatforms}
          value={filters}
          onChange={setFilters}
        />
      </div>

      {/* 移动端筛选抽屉 */}
      <MobileFiltersDrawer open={drawerOpen} onClose={() => setDrawerOpen(false)}>
        <Filter_Store
          loading={filterLoading}
          stores={filterOpts.stores}
          platforms={allowedPlatforms}
          value={filters}
          onChange={setFilters}
        />
      </MobileFiltersDrawer>
    </div>
  );
}
